<script setup lang="ts">
import WrIcon from "~/components/global/WrIcon.vue";

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== 'dark');

interface TransformCard {
  title: string;
  description: string;
  icon?: string;
  svgIcon?: string;
}

const emit = defineEmits<{ 'cta-clicked': []; }>();


const props = withDefaults(defineProps<{
  heading?: string;
  subtitle?: string;
  cards?: TransformCard[];
  ctaText?: string;
  ctaLink?: string | null;
}>(), {
  heading: 'How AI Transforms Creative Processes',
  subtitle: 'AI accelerates creativity with smarter workflows, faster execution, and sharper outcomes.',
  cards: () => [],
  ctaText: 'See AI in Action',
  ctaLink: null,
});
</script>

<template>
  <section class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]" :aria-label="heading">
    <!-- Section heading -->
    <div class="text-center mb-14">
      <h2
        class="text-[28px] lg:text-[48px] font-semibold leading-tight font-avant text-transparent bg-clip-text bg-gradient-to-r from-[#12DBFF] to-[#BA55FE]"
      >
        {{ heading }}
      </h2>
      <p
        class="mt-4 text-[16px] lg:text-[18px] font-normal tracking-[0.36px] max-w-2xl mx-auto font-avant"
        :class="isLight ? 'text-black/70' : 'text-white/70'"
      >
        {{ subtitle }}
      </p>
    </div>

    <!-- Grid container -->
    <div class="relative mt-10 px-4 max-w-[800px] mx-auto">
      <!-- Grid overlay: lines + crosshairs behind cards (md+ only) -->
      <div class="hidden md:block absolute inset-0 pointer-events-none grid-overlay">
        <span class="absolute h-grid-line" style="top:0; left:var(--grid-offset); right:var(--grid-offset)" />
        <span class="absolute h-grid-line" style="top:50%; left:var(--grid-offset); right:var(--grid-offset)" />
        <span class="absolute h-grid-line" style="bottom:0; left:var(--grid-offset); right:var(--grid-offset)" />
        <span class="absolute v-grid-line" style="left:0; top:var(--grid-offset); bottom:var(--grid-offset)" />
        <span class="absolute v-grid-line" style="left:50%; top:var(--grid-offset); bottom:var(--grid-offset)" />
        <span class="absolute v-grid-line" style="right:0; top:var(--grid-offset); bottom:var(--grid-offset)" />
        <span class="absolute crosshair" style="top:0; left:0" />
        <span class="absolute crosshair" style="top:0; right:0" />
        <span class="absolute crosshair" style="bottom:0; left:0" />
        <span class="absolute crosshair" style="bottom:0; right:0" />
        <span class="absolute crosshair" style="top:0; left:50%" />
        <span class="absolute crosshair" style="top:50%; left:0" />
        <span class="absolute crosshair" style="top:50%; right:0" />
        <span class="absolute crosshair" style="bottom:0; left:50%" />
        <span class="absolute crosshair" style="top:50%; left:50%" />
      </div>

      <!-- Cards Grid -->
      <div class="relative grid grid-cols-1 md:grid-cols-2">
        <article
          v-for="(card, index) in cards"
          :key="index"
          class="flex flex-row items-center p-6 md:flex-col md:items-start justify-center md:p-6 ai-transform-card"
          :class="index < cards.length - 1 ? 'border-b border-white/10 md:border-b-0' : ''"
        >
          <WrIcon
            v-if="card?.svgIcon || card?.icon"
            :name="(card.svgIcon || card.icon)!"
            class="flex-shrink-0 size-6 md:size-8 mr-6 md:mr-0 md:mb-4"
            :class="isLight ? 'text-[#613FAA]' : 'text-white'"
          />
          <div class="flex flex-col min-w-0">
            <h3
              class="font-avant text-[18px] font-semibold leading-normal"
              :class="isLight ? 'text-[#613FAA]' : 'text-[#F0DAFF]'"
            >
              {{ card.title }}
            </h3>
            <p
              class="mt-2 text-[15px] leading-5 font-avant"
              :class="isLight ? 'text-black' : 'text-[rgba(255,255,255,0.85)]'"
            >
              {{ card.description }}
            </p>
          </div>
        </article>
      </div>
    </div>

    <!-- CTA Button -->
    <div v-if="ctaText" class="relative flex justify-center mt-12 md:mt-20 z-20">
      <WrButton
        :link="ctaLink || undefined"
        @click="!ctaLink ? emit('cta-clicked') : undefined"
        container-class="h-[48px]"
        append-class="text-base font-normal px-6"
      >
        {{ ctaText }}
      </WrButton>
    </div>
  </section>
</template>

<style scoped>
.grid-overlay {
  --grid-offset: -72px;
}

.ai-transform-card {
  width: 100%;
}

@media (min-width: 1024px) {
  .ai-transform-card {
    width: 383px;
    height: 246px;
  }
}

.h-grid-line {
  height: 2px;
  background: linear-gradient(
    to right,
    transparent 0%,
    rgba(58, 42, 123, 0.7) 11.6261%,
    rgba(58, 42, 123, 0.7) 93.2692%,
    transparent 100%
  );
}

.v-grid-line {
  width: 2px;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(58, 42, 123, 0.7) 11.6261%,
    rgba(58, 42, 123, 0.7) 93.2692%,
    transparent 100%
  );
}

.crosshair::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 16px;
  height: 2px;
  background: #7560c9;
  transform: translate(-50%, -50%);
}

.crosshair::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 2px;
  height: 16px;
  background: #7560c9;
  transform: translate(-50%, -50%);
}
</style>
